import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Plus, Eye, Edit, Trash2, Search, UserPlus, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  intake: "bg-blue-50 text-blue-700",
  active: "bg-emerald-50 text-emerald-700",
  completed: "bg-warm-50 text-warm-700",
  withdrawn: "bg-amber-50 text-amber-700",
  closed: "bg-gray-100 text-gray-600",
};

export const ParticipantsPanel = ({ getAuthHeader, onRefresh }) => {
  const [participants, setParticipants] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({
    first_name: "", last_name: "", date_of_birth: "", email: "", phone: "",
    address: "", city: "", state: "MD", zip_code: "",
    emergency_contact_name: "", emergency_contact_phone: "",
    referral_source: "self", case_number: "", assigned_program: "", notes: "",
  });

  const fetchParticipants = async () => {
    try {
      const res = await axios.get(`${API}/admin/participants`, getAuthHeader());
      setParticipants(res.data);
      setLoaded(true);
    } catch { toast.error("Failed to load participants"); }
  };

  if (!loaded) { fetchParticipants(); }

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/participants`, form, getAuthHeader());
      toast.success("Participant added");
      setShowForm(false);
      setForm({ first_name: "", last_name: "", date_of_birth: "", email: "", phone: "", address: "", city: "", state: "MD", zip_code: "", emergency_contact_name: "", emergency_contact_phone: "", referral_source: "self", case_number: "", assigned_program: "", notes: "" });
      fetchParticipants();
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add participant"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this participant and all related records?")) return;
    try {
      await axios.delete(`${API}/admin/participants/${id}`, getAuthHeader());
      toast.success("Deleted");
      fetchParticipants();
      if (onRefresh) onRefresh();
    } catch { toast.error("Failed to delete"); }
  };

  const viewDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/participants/${id}`, getAuthHeader());
      setDetail(res.data);
    } catch { toast.error("Failed to load details"); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/admin/participants/${id}`, { status: newStatus }, getAuthHeader());
      toast.success("Status updated");
      fetchParticipants();
    } catch { toast.error("Failed to update"); }
  };

  const filtered = participants.filter(p =>
    `${p.first_name} ${p.last_name} ${p.email} ${p.case_number}`.toLowerCase().includes(search.toLowerCase())
  );

  if (detail) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => setDetail(null)} className="mb-2">Back to List</Button>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">{detail.first_name} {detail.last_name}</h3>
            <p className="text-sm text-gray-500">Status: <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[detail.status]}`}>{detail.status}</span></p>
            {detail.phone && <p className="text-sm">Phone: {detail.phone}</p>}
            {detail.email && <p className="text-sm">Email: {detail.email}</p>}
            {detail.case_number && <p className="text-sm">Case #: {detail.case_number}</p>}
            {detail.assigned_program && <p className="text-sm">Program: {detail.assigned_program}</p>}
            {detail.referral_source && <p className="text-sm">Referral: {detail.referral_source}</p>}
            {detail.address && <p className="text-sm">Address: {detail.address}, {detail.city}, {detail.state} {detail.zip_code}</p>}
            {detail.emergency_contact_name && <p className="text-sm">Emergency: {detail.emergency_contact_name} ({detail.emergency_contact_phone})</p>}
            <p className="text-sm font-medium mt-2">Total Hours Attended: {detail.total_hours_attended || 0}</p>
          </div>
          <div>
            <h4 className="font-medium text-sm text-gray-700 mb-2">Cases ({detail.cases?.length || 0})</h4>
            {detail.cases?.length ? detail.cases.map(c => (
              <div key={c.id} className="bg-gray-50 p-3 rounded-lg mb-2 text-sm">
                <p className="font-medium">{c.case_number} - {c.program}</p>
                <p className="text-gray-500">Status: {c.status} | Start: {c.start_date}</p>
              </div>
            )) : <p className="text-sm text-gray-400">No cases yet</p>}

            <h4 className="font-medium text-sm text-gray-700 mb-2 mt-4">Recent Attendance ({detail.attendance_records?.length || 0})</h4>
            {detail.attendance_records?.slice(0, 5).map(a => (
              <div key={a.id} className="bg-gray-50 p-2 rounded-lg mb-1 text-xs">
                {a.date} - {a.status} ({a.hours}h)
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="participant-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search participants..." className="pl-10" />
        </div>
        <Button data-testid="add-participant-btn" onClick={() => setShowForm(!showForm)} className="bg-warm-700 hover:bg-warm-800 text-white gap-2">
          {showForm ? <ChevronUp className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
          {showForm ? "Cancel" : "New Intake"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-gray-50 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div><Label className="text-xs">First Name *</Label><Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} required className="mt-1" /></div>
          <div><Label className="text-xs">Last Name *</Label><Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} required className="mt-1" /></div>
          <div><Label className="text-xs">Date of Birth</Label><Input type="date" value={form.date_of_birth} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Referral Source</Label>
            <select value={form.referral_source} onChange={e => setForm({ ...form, referral_source: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
              <option value="self">Self</option><option value="cps">CPS/DHS</option><option value="court">Court</option><option value="school">School</option><option value="partner">Partner Agency</option><option value="other">Other</option>
            </select>
          </div>
          <div><Label className="text-xs">Case Number</Label><Input value={form.case_number} onChange={e => setForm({ ...form, case_number: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Assigned Program</Label>
            <select value={form.assigned_program} onChange={e => setForm({ ...form, assigned_program: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Select...</option><option value="Parenting Support Program">Parenting Support Program</option><option value="Violence Intervention">Violence Intervention</option><option value="Family Counseling">Family Counseling</option><option value="Youth Mentorship">Youth Mentorship</option><option value="Community Support">Community Support</option>
            </select>
          </div>
          <div><Label className="text-xs">Address</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">State</Label><Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Zip</Label><Input value={form.zip_code} onChange={e => setForm({ ...form, zip_code: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Emergency Contact</Label><Input value={form.emergency_contact_name} onChange={e => setForm({ ...form, emergency_contact_name: e.target.value })} placeholder="Name" className="mt-1" /></div>
          <div><Label className="text-xs">Emergency Phone</Label><Input value={form.emergency_contact_phone} onChange={e => setForm({ ...form, emergency_contact_phone: e.target.value })} className="mt-1" /></div>
          <div className="sm:col-span-2 lg:col-span-3"><Label className="text-xs">Notes</Label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" /></div>
          <div><Button type="submit" className="bg-warm-700 hover:bg-warm-800 text-white">Save Participant</Button></div>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-100">
            <th className="text-left py-3 px-4 text-gray-500 font-medium">Name</th>
            <th className="text-left py-3 px-4 text-gray-500 font-medium hidden md:table-cell">Program</th>
            <th className="text-left py-3 px-4 text-gray-500 font-medium hidden sm:table-cell">Referral</th>
            <th className="text-left py-3 px-4 text-gray-500 font-medium">Status</th>
            <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                <td className="py-3 px-4">
                  <p className="font-medium text-gray-900">{p.first_name} {p.last_name}</p>
                  <p className="text-xs text-gray-400">{p.case_number || "No case #"}</p>
                </td>
                <td className="py-3 px-4 text-gray-600 hidden md:table-cell">{p.assigned_program || "-"}</td>
                <td className="py-3 px-4 text-gray-600 hidden sm:table-cell capitalize">{p.referral_source}</td>
                <td className="py-3 px-4">
                  <select value={p.status} onChange={e => handleStatusChange(p.id, e.target.value)} className={`text-xs px-2 py-1 rounded-full font-medium border-0 cursor-pointer ${statusColors[p.status]}`}>
                    <option value="intake">Intake</option><option value="active">Active</option><option value="completed">Completed</option><option value="withdrawn">Withdrawn</option><option value="closed">Closed</option>
                  </select>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => viewDetail(p.id)} className="h-8 w-8 p-0"><Eye className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="py-8 text-center text-gray-400">No participants found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
